/**
 * Product Utilities Module
 *
 * Centralized variant resolution for all product components. Provides URL
 * construction, cached fetching, in-flight deduplication, and prefetching
 * for Shopify's product-variant-json alternate template.
 *
 * Usage:
 * import { fetchVariantData, prefetchVariantData, buildProductUrl } from '@utilities/product';
 */

/** @type {Map<string, Object>} Shared cross-component variant data cache */
const cache = new Map();

/** @type {Map<string, Promise<Object|null>>} In-flight requests for deduplication */
const pending = new Map();

/**
 * Build a product URL with optional view and section_id parameters.
 *
 * @param {string} baseUrl - The product URL (e.g., /products/my-product)
 * @param {string[]} [optionValueIds=[]] - Array of selected option value IDs
 * @param {Object} [options={}]
 * @param {string} [options.view='product-variant-json'] - Alternate template view
 * @param {string} [options.sectionId] - Section ID for section rendering
 * @param {string|number} [options.variantId] - When optionValueIds is empty, resolve via ?variant=
 * @returns {string|null} Fully constructed URL or null if baseUrl is missing
 */
export function buildProductUrl(baseUrl, optionValueIds = [], options = {}) {
  if (!baseUrl) return null;

  const { view = 'product-variant-json', sectionId, variantId } = options;

  const url = new URL(baseUrl, window.location.origin);

  if (optionValueIds.length > 0) {
    url.searchParams.set('option_values', optionValueIds.join(','));
    url.searchParams.delete('variant');
  } else {
    url.searchParams.delete('option_values');
    if (variantId != null && String(variantId).trim() !== '') {
      url.searchParams.set('variant', String(variantId));
    }
  }

  if (view) {
    url.searchParams.set('view', view);
  } else {
    url.searchParams.delete('view');
  }

  if (sectionId) {
    url.searchParams.set('section_id', sectionId);
  } else {
    url.searchParams.delete('section_id');
  }

  return url.toString();
}

/**
 * Fetch variant data with caching and in-flight deduplication.
 *
 * Uses a single global cache shared across all components on the page,
 * so a product card and a quickview modal requesting the same product/options
 * share one cached response. Concurrent requests for the same URL are
 * coalesced into a single network call.
 *
 * @param {string} baseUrl - The product URL
 * @param {string[]} [optionValueIds=[]] - Array of selected option value IDs
 * @param {Object} [options={}] - Passed to buildProductUrl (e.g. variantId when no option radios)
 * @returns {Promise<Object|null>} Parsed variant data or null on failure
 */
export async function fetchVariantData(baseUrl, optionValueIds = [], options = {}) {
  const fetchUrl = buildProductUrl(baseUrl, optionValueIds, options);
  if (!fetchUrl) return null;

  if (cache.has(fetchUrl)) {
    return cache.get(fetchUrl);
  }

  if (pending.has(fetchUrl)) {
    return pending.get(fetchUrl);
  }

  const request = (async () => {
    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) return null;
      const data = await response.json();
      cache.set(fetchUrl, data);
      return data;
    } catch (error) {
      console.error('utilities-product.js: fetchVariantData() failed', error);
      return null;
    } finally {
      pending.delete(fetchUrl);
    }
  })();

  pending.set(fetchUrl, request);
  return request;
}

/**
 * Prefetch variant data. No-op if the URL is already cached or in-flight.
 * Silently swallows errors since this is a best-effort optimization.
 *
 * @param {string} baseUrl - The product URL
 * @param {string[]} [optionValueIds=[]] - Array of selected option value IDs
 * @param {Object} [options={}] - Passed to buildProductUrl
 */
export async function prefetchVariantData(baseUrl, optionValueIds = [], options = {}) {
  const fetchUrl = buildProductUrl(baseUrl, optionValueIds, options);
  if (!fetchUrl || cache.has(fetchUrl) || pending.has(fetchUrl)) return;

  try {
    await fetchVariantData(baseUrl, optionValueIds, options);
  } catch {
    // Prefetch failures are silent
  }
}

/**
 * Normalize a variant identifier to a positive integer.
 *
 * @param {string|number|null|undefined} variantId - Raw variant identifier
 * @returns {number|null} Positive integer variant ID, or null if invalid
 */
export function normalizeVariantId(variantId) {
  if (variantId === null || variantId === undefined) return null;
  if (typeof variantId === 'number') {
    return Number.isInteger(variantId) && variantId > 0 ? variantId : null;
  }

  const parsed = Number.parseInt(String(variantId).trim(), 10);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
}

/**
 * Whether a variant can be added to cart (Shopify inventory AND Greyson availability).
 *
 * @param {Object|null|undefined} variant - Variant object from product-variant-json
 * @returns {boolean}
 */
export function isVariantFullyAvailable(variant) {
  if (!variant?.id) return false;
  return variant.available_shopify === true && variant.available_greyson === true;
}

